import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FiltersContext,
  type FiltersState,
} from "../FilterProvider/FilterProvider";
import "./FiltersMenu.scss";
import FiltersMenuPopup from "./FiltersMenuPopup/FiltersMenuPopup";
import { useQuery } from "@apollo/client";
import {
  GET_FINDING_FILTERS,
  type GetFindingFiltersVars,
} from "../../../Findings/FindingsFilters/FindingsFilterApi";
import { findingsQueryParams } from "../../../Findings/FindingsUtils";
import { filtersDataToFiltersList } from "../../../Findings/FindingsFilters/FilterUtils";
import useFindingsQueryParam from "../../../Findings/FindingsData/useFindingsQueryParam";
import { useFormContext } from "react-hook-form";
import { useRouteMatch } from "react-router-dom";
import { filterTabs } from "../../AppViewUtils";
import {
  Tooltip,
  FieldViewBlock,
  SeemPopover,
  DotLoader,
} from "@SeemplicityDev/shared";
import Chip from "../../../Components/Chip/Chip";
import type { FilterObject } from "../../FilterManagement/CommonFilterTypes";
import {
  FILTER_TO_EXECUTE_PARSE_ERROR,
  parseFilterQuery,
  safeRemoveStoredFilter,
} from "../parseFilterQuery";
import useLogger, { LOG_SEVERITY } from "../../../Logger/useLogger";
import { useSavedFilterTypeState } from "../useSavedFilterType";

export const EMPTY_FILTER = "EMPTY";

interface FiltersMenuProps {
  setDefaultsForScopeAndFilter?: boolean;
  onLoadingFiltersData: (isLoading: boolean) => void;
  readOnly?: boolean;
  disabled?: boolean;
  displayAsInput?: boolean;
  displayMenuHeader?: boolean;
  useSameWidth?: boolean;
  viewMode?: boolean;
  locationContext?: string;
}

// Minimal shape of the GET_FINDING_FILTERS query response consumed here.
// FilterUtils.js (filtersDataToFiltersList) is still untyped JS.
interface FindingFiltersData {
  finding_filters?: {
    edges?: unknown[];
  };
}

const FiltersMenu: React.FC<FiltersMenuProps> = ({
  setDefaultsForScopeAndFilter,
  onLoadingFiltersData,
  readOnly,
  disabled,
  displayAsInput,
  displayMenuHeader,
  useSameWidth,
  viewMode = false,
  locationContext,
}) => {
  const [isOpen, toggleIsOpen] = useState(false);
  const {
    filtersState: {
      filtersList,
      defaultFilter,
      selectedFilter,
      shouldOpenFiltersMenu,
    },
    updateFiltersState,
    getFilterValueById,
    lsFilterKey,
    filtersEntity,
  } = useContext(FiltersContext);
  const { savedFilterType, isSavedFilterTypeKnown, supportsSavedFilters } =
    useSavedFilterTypeState(filtersEntity);
  let match = useRouteMatch<{ item: string }>("/:item");

  const {
    formState: { isDirty },
  } = useFormContext();

  const { logError } = useLogger();

  const { search } = useFindingsQueryParam(null);
  const { param: selectedFilterIdFromURL } = useFindingsQueryParam(
    findingsQueryParams.FILTER_ID
  );
  const { param: filterToExecuteFromURL } = useFindingsQueryParam(
    findingsQueryParams.FILTER_TO_EXECUTE
  );

  const { data: filtersData, loading: isFiltersQueryLoading } = useQuery<
    FindingFiltersData,
    GetFindingFiltersVars
  >(GET_FINDING_FILTERS, {
    variables: { filterType: savedFilterType },
    skip: !supportsSavedFilters || !isSavedFilterTypeKnown,
  });
  const isLoadingFilters =
    supportsSavedFilters && (isFiltersQueryLoading || !isSavedFilterTypeKnown);

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  // Restore after page components have set their own default filter.
  const isReadyToRestoreFilter = supportsSavedFilters
    ? !!filtersList.length
    : hasMounted;

  const setFiltersFromURL = () => {
    // selectedFilterIdFromURL may be null; getFilterValueById's find() returns
    // undefined for a null id, matching the original JS runtime behavior. Cast
    // to satisfy the string param without altering what is passed.
    const filterExists = getFilterValueById(
      selectedFilterIdFromURL as unknown as string
    );
    const selectedFilter = filterExists ? filterExists : defaultFilter;

    updateFiltersState({
      selectedFilter: selectedFilter,
      filterToExecute: selectedFilter?.value.query,
    });
  };

  useEffect(() => {
    onLoadingFiltersData(isLoadingFilters);
  }, [isLoadingFilters]);

  const setFiltersFromLocalStorage = () => {
    let lsFilter: FilterObject | null = null;
    try {
      lsFilter = JSON.parse(localStorage.getItem(lsFilterKey) ?? "null");
    } catch {
      lsFilter = null;
    }

    if (!lsFilter) {
      return;
    }

    // Only restore if defaults apply or the user had unsaved changes
    if (!setDefaultsForScopeAndFilter && !lsFilter?.value?.isDirty) {
      return;
    }

    let selectedFilter;
    if (!!lsFilter?.value?.id) {
      selectedFilter = getFilterValueById(lsFilter.value.id)
        ? lsFilter
        : defaultFilter;
    } else {
      selectedFilter = lsFilter;
    }

    const filterToExecute =
      (selectedFilter as FilterObject & { filterToExecute?: string })
        ?.filterToExecute ?? selectedFilter?.value?.query;

    const { parseError } = parseFilterQuery(filterToExecute);
    if (parseError) {
      safeRemoveStoredFilter(lsFilterKey);
      logError(parseError, FILTER_TO_EXECUTE_PARSE_ERROR, {
        severity: LOG_SEVERITY.CRITICAL,
        extra: { lsFilterKey, source: "localStorage" },
      });
      updateFiltersState({
        selectedFilter: defaultFilter,
        filterToExecute: defaultFilter?.value?.query,
        pendingFilterToExecute: undefined,
        isDirty: false,
      });
      return;
    }

    updateFiltersState({
      selectedFilter,
      filterToExecute,
    });
  };

  useEffect(() => {
    if (isReadyToRestoreFilter) {
      if (!!selectedFilterIdFromURL) {
        setFiltersFromURL();
      } else if (!search && localStorage.getItem(lsFilterKey)) {
        setFiltersFromLocalStorage();
      } else {
        if (setDefaultsForScopeAndFilter) {
          let filter: Partial<FiltersState> = {};

          if (!selectedFilter && !!defaultFilter && !filterToExecuteFromURL) {
            filter = {
              selectedFilter: defaultFilter,
              filterToExecute: defaultFilter?.value?.query,
            };
          }

          updateFiltersState(filter);
        } else if (!setDefaultsForScopeAndFilter && !selectedFilter) {
          updateFiltersState({
            selectedFilter: {
              label: EMPTY_FILTER,
            } as FilterObject,
          });
        }
      }
    }
  }, [
    selectedFilterIdFromURL,
    filtersList,
    defaultFilter,
    setDefaultsForScopeAndFilter,
    isReadyToRestoreFilter,
  ]);

  useEffect(() => {
    if (!!filterToExecuteFromURL && isReadyToRestoreFilter) {
      const filterObj: Partial<FiltersState> = {};
      // useFindingsQueryParam.param is typed string | string[]; this URL param is
      // a single string at runtime, so narrow with `as string` (preserves value).
      filterObj.filterToExecute = filterToExecuteFromURL as string;
      filterObj.selectedFilter = {
        label: "Filter By",
        value: {
          query: filterToExecuteFromURL as string,
          editable: true,
        },
      };
      updateFiltersState(filterObj);
    }
  }, [filterToExecuteFromURL, filtersList, isReadyToRestoreFilter]);

  const getFilters = useCallback(():
    | Pick<FiltersState, "filtersList" | "defaultFilter" | "filtersMenuTab">
    | undefined => {
    if (
      !!filtersData &&
      !!filtersData.finding_filters &&
      !!filtersData.finding_filters.edges
    ) {
      const [findingsFilterData, FILTER_ALL_OPEN] = filtersDataToFiltersList(
        // Local FindingFiltersData is a looser duplicate of the (un-exported)
        // shape filtersDataToFiltersList expects; the guard above confirms
        // finding_filters.edges exist, so cast at the boundary.
        filtersData as Parameters<typeof filtersDataToFiltersList>[0],
        savedFilterType
      ) as [FilterObject[], FilterObject | null];

      if (!FILTER_ALL_OPEN) {
        throw new Error("Default filter is missing");
      }

      const hasSavedFilters = findingsFilterData?.find(
        (filter) => !filter?.meta?.system
      );

      return {
        filtersList: findingsFilterData,
        defaultFilter: FILTER_ALL_OPEN,
        filtersMenuTab: !!hasSavedFilters
          ? filterTabs.SAVED
          : filterTabs.RECOMMENDED,
      };
    }
  }, [filtersData, savedFilterType]);

  useEffect(() => {
    const filters = getFilters();

    updateFiltersState(filters ?? {});
  }, [filtersData, getFilters]);

  const popoverTitle = useMemo(() => {
    return selectedFilter?.label === EMPTY_FILTER
      ? "Select Filter"
      : !!selectedFilter?.label
        ? `${isDirty ? "* " : ""}${selectedFilter?.label}`
        : "Filter By";
  }, [selectedFilter, isDirty]);

  const isRecommendedSelected = useMemo(
    () => selectedFilter?.meta?.system,
    [selectedFilter]
  );

  useEffect(() => {
    updateFiltersState({ isDirty });
  }, [isDirty]);

  useEffect(() => {
    if (shouldOpenFiltersMenu) {
      toggleIsOpen(true);
      updateFiltersState({ shouldOpenFiltersMenu: false });
    }
  }, [shouldOpenFiltersMenu, updateFiltersState]);

  return !viewMode ? (
    <SeemPopover
      isOpen={isOpen}
      placement={"bottom-start"}
      triggerClassName={"filters-menu-popover-trigger-wrap"}
      body={[
        <FiltersMenuPopup
          key="filters-menu-popup"
          toggleIsOpen={toggleIsOpen}
          setDefaultsForScopeAndFilter={setDefaultsForScopeAndFilter}
          displayMenuHeader={displayMenuHeader}
        />,
      ]}
      onOuterAction={() => setTimeout(() => toggleIsOpen(false), 200)}
      className={`filter-popover-class-${match?.params?.item}${
        locationContext ? ` ${locationContext}` : ""
      }`}
      useSameWidth={useSameWidth}
    >
      <div
        data-testid={`filters-menu`}
        className={`filters-menu-content ${
          displayAsInput ? "input-holder" : ""
        } ${isLoadingFilters ? "is-loading" : ""}`}
      >
        <div
          className={`filters-menu-content-selected ${
            isOpen ? "is-active" : ""
          } ${readOnly ? "read-only" : ""}`}
          data-testid={"filters-menu-content-selected"}
          onClick={(e) => {
            if (!readOnly && !disabled) {
              toggleIsOpen(!isOpen);
            }
          }}
        >
          <div className={"input-holder-icon"}>
            <i
              className={`${
                isRecommendedSelected
                  ? "seem-icon-filter-recommended"
                  : "seem-icon-filter"
              }`}
            ></i>{" "}
          </div>
          <Tooltip content={popoverTitle}>
            <div
              data-testid={`selected-filter-title`}
              className={`input-holder-text selected-filter-title ${
                selectedFilter?.label === EMPTY_FILTER ? "placeholder-text" : ""
              }`}
            >
              {isLoadingFilters ? (
                <DotLoader className={"filters-menu-loader"} />
              ) : (
                popoverTitle
              )}
            </div>
          </Tooltip>
          {!readOnly && !disabled && !isLoadingFilters && (
            <div className={`dropdown-indicator`}>
              <i className={`seem-icon seem-icon-chevron-down`} />
            </div>
          )}
        </div>
      </div>
    </SeemPopover>
  ) : (
    <FieldViewBlock label="Filter" data-testid="filters-menu">
      <Chip
        leftIcon={"seem-icon-filter"}
        text={selectedFilter?.label}
        noHover
        noMargin
      />
    </FieldViewBlock>
  );
};

export default FiltersMenu;
